import {
  cloneElement,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import {
  folders,
  othersSnippets,
  pinnedSnippets,
  resetFilter,
  search,
  setFolderFilter,
  snippets,
  toggleCmdkPopup,
} from "rtk-slice"

import { handleNoneEmptyFolders } from "utils/folders"
import { isMobile } from "utils/mediaQuery"

import {
  AppBar,
  Badge,
  Chip,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useScrollTrigger,
  useTheme,
} from "@mui/material"

import {
  FolderCodeSolid,
  FolderFilled,
  Search,
  Snippet,
  SnippifyLogo,
} from "ui-icons"

function ElevationScroll(props) {
  const { children, main, setScroll } = props
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  })

  useEffect(() => {
    setScroll(trigger)
  }, [setScroll, trigger])
  return cloneElement(children, {
    elevation: 0,
    sx: {
      bgcolor: "transparent",
      background: !trigger ? "transparent" : "hsl(30% 70% 10% / 0.6)",
      backdropFilter: !trigger ? "none" : "blur(10px)",
      "& span": {
        color: !trigger ? "#454545" : main,

        lineHeight: "1.5rem",
        fontWeight: "bold",
      },
      zIndex: 1000,
    },
  })
}

const Header = () => {
  const dispatch = useDispatch()
  const {
    palette: {
      mode,
      primary: { main },
      text: { primary, cancel },
    },
  } = useTheme()

  const [scroll, setScroll] = useState(false)

  const { pathname } = useLocation()
  const pinnedSnippetsState = useSelector(pinnedSnippets)
  const othersSnippetsState = useSelector(othersSnippets)

  const filteredSnippetsLength = useMemo(
    () => pinnedSnippetsState.length + othersSnippetsState.length,
    [othersSnippetsState.length, pinnedSnippetsState.length]
  )

  const searchState = useSelector(search)
  const foldersState = useSelector(folders)
  const snippetsState = useSelector(snippets)

  const noneEmptyFoldersLength = useMemo(
    () =>
      handleNoneEmptyFolders([...snippetsState], foldersState).length,

    [foldersState, snippetsState]
  )

  const resetAllFilters = () => {
    dispatch(resetFilter())
  }

  const resetFolderFilter = () => {
    dispatch(setFolderFilter(""))
  }

  const handleCmdkDialog = () => {
    dispatch(toggleCmdkPopup())
  }

  useEffect(() => {
    const cmdkTrigger = (e) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "k" &&
        (pathname === "/" || pathname === "/snippets")
      ) {
        e.preventDefault()
        e.stopPropagation()
        dispatch(toggleCmdkPopup())
      }
    }
    document.addEventListener("keydown", cmdkTrigger)
    return () => {
      document.removeEventListener("keydown", cmdkTrigger)
    }
  }, [dispatch, pathname])

  return (
    <ElevationScroll
      primary={primary}
      main={main}
      setScroll={setScroll}
    >
      <AppBar>
        <Toolbar
          sx={{
            justifyContent: "space-between",
            top: scroll ? 0 : 20,
            alignItems: "center",
            transition: "all 0.5s ease-in-out",
          }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing="-0.85em"
          >
            <Stack
              sx={{
                animation: `spin 2.5s linear ${
                  scroll ? "infinite" : "none"
                } both`,
              }}
              justifyContent="center"
              alignItems="center"
            >
              <SnippifyLogo width="60" height="60" fill={main} />
            </Stack>
            <Stack
              direction="row"
              sx={{
                gap: "0.25em",
                minHeight: "5em",
                position: "relative",
              }}
            >
              <Typography
                variant="h4"
                fontFamily="Goldman, cursive "
                component="span"
                className="logo"
                sx={{
                  fontWeight: 900,
                  fontStyle: scroll ? "italic" : "inherit",
                  alignSelf: "center",
                  cursor: "pointer",
                }}
                onClick={resetAllFilters}
              >
                nippify
              </Typography>
              <strong
                style={{
                  fontSize: "1rem",

                  color: cancel,
                  alignSelf: "flex-end",

                  position: "absolute",

                  left: scroll ? "6.5em" : "0.25em",

                  bottom: scroll ? "0.8em" : "0.4em",
                  transition: "all 1s",
                }}
              >
                beta
              </strong>
            </Stack>
          </Stack>
          {isMobile() || scroll ? null : (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "center",
                marginRight: "auto",
                px: "1em",
                height: "3em",
                alignSelf: "center",
              }}
            >
              <Divider orientation="vertical" />
              <Typography
                fontFamily="Satisfy, cursive "
                component="span"
                sx={{
                  color: "#c51162 !important",

                  fontWeight: "normal !important",
                  fontSize: "1.5rem",
                  marginRight: "auto",
                  px: "0.5em",
                }}
              >
                Explain your code
              </Typography>
            </Stack>
          )}
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              direction="row"
              flex={0.5}
              sx={{
                mr: "auto !important",
              }}
            >
              {window.location.pathname === "/snippets" ? (
                <Tooltip
                  title={
                    foldersState.length >= 1
                      ? noneEmptyFoldersLength === 1
                        ? "1 None-Empty Folder"
                        : noneEmptyFoldersLength > 1
                        ? `${noneEmptyFoldersLength} None-Empty Folders`
                        : "All folders are empty"
                      : "You have no folders yet"
                  }
                >
                  <IconButton
                    to={
                      window.location.search
                        ? `/${window.location.search}`
                        : "/"
                    }
                    onClick={resetFolderFilter}
                  >
                    <Badge
                      badgeContent={noneEmptyFoldersLength}
                      color="error"
                      overlap="circular"
                      variant="dot"
                      disabled={!noneEmptyFoldersLength}
                    >
                      {noneEmptyFoldersLength ? (
                        <FolderFilled
                          tags={mode === "light" ? "#fff" : "#575758"}
                          fill={main}
                          _fill={scroll ? "#fff" : "#575758"}
                          width="30"
                          height="30"
                        />
                      ) : (
                        <FolderCodeSolid />
                      )}
                    </Badge>
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip
                title={
                  filteredSnippetsLength === 1
                    ? "1 Snippet"
                    : `${filteredSnippetsLength} Snippets`
                }
              >
                <IconButton
                  to={
                    searchState
                      ? `snippets?keyword=${searchState}`
                      : "/snippets"
                  }
                >
                  <Badge
                    badgeContent={filteredSnippetsLength}
                    overlap="circular"
                    color="error"
                    max={99}
                    showZero
                  >
                    <Snippet
                      _fill={main}
                      fill={"#575758"}
                      width="30"
                      height="30"
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack
              justifySelf="flex-start"
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              sx={{
                height: "3em",
                borderRadius: "2.5em",
                px: "1em",
                cursor: "pointer",
                opacity: "0.8",
                mx: "0.5em !important",
                border: `1px solid transparent`,
                "&:hover": {
                  borderColor: cancel,
                  opacity: 1,
                },
              }}
              onClick={handleCmdkDialog}
            >
              <Search
                fill={main}
                stroke={primary}
                width="24"
                height="24"
              />
              {isMobile() ? null : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: cancel,
                    }}
                  >
                    Search...
                  </Typography>
                  <Chip
                    label="Ctrl+k"
                    sx={{
                      borderRadius: 1,
                      "& .MuiChip-label": {
                        px: "0.5em",
                        color: cancel,
                      },
                      height: "1.75em",
                    }}
                  />
                </>
              )}
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
    </ElevationScroll>
  )
}

export default memo(Header)
